/**
 * Frontend mirror of backend helpdeskCatalogFlatten — Type → Category only.
 */

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj || {}));
}

function hasNestedSubcategories(categories) {
  return (categories || []).some(
    (c) => Array.isArray(c.subcategories) && c.subcategories.length > 0
  );
}

export function flattenHelpdeskConfigToTwoLevels(config) {
  if (!config || typeof config !== "object") {
    return { config, changed: false, idMap: [] };
  }

  const next = deepClone(config);
  const categories = Array.isArray(next.masters?.categories)
    ? next.masters.categories
    : [];

  const alreadyFlat =
    next.catalogLevels === 2 &&
    !hasNestedSubcategories(categories) &&
    Number(next.version) >= 4;

  if (alreadyFlat) {
    next.masters = next.masters || {};
    next.masters.categories = categories.map((c) => {
      const { subcategories, ...rest } = c || {};
      return { ...rest, enabled: c?.enabled !== false };
    });
    return { config: next, changed: false, idMap: next._legacySubcategoryMap || [] };
  }

  const usedIds = new Set();
  const newCats = [];
  const idMap = [];

  for (const c of categories) {
    if (!c || !c.id) continue;
    const subs = Array.isArray(c.subcategories) ? c.subcategories : [];
    if (subs.length === 0) {
      let id = String(c.id);
      if (usedIds.has(id)) id = `${id}_${usedIds.size}`;
      usedIds.add(id);
      newCats.push({
        id,
        typeId: c.typeId,
        name: c.name || id,
        enabled: c.enabled !== false,
      });
      continue;
    }

    for (const s of subs) {
      if (!s || !s.id) continue;
      let newId = String(s.id);
      if (usedIds.has(newId)) newId = `${c.id}__${s.id}`;
      usedIds.add(newId);
      newCats.push({
        id: newId,
        typeId: c.typeId,
        name: s.name || newId,
        enabled: s.enabled !== false,
      });
      idMap.push({
        oldCategoryId: String(c.id),
        oldSubId: String(s.id),
        newCategoryId: newId,
        typeId: c.typeId,
      });
    }
  }

  const policiesIn = Array.isArray(next.levelPolicies) ? next.levelPolicies : [];
  const policiesOut = [];

  for (const p of policiesIn) {
    if (!p) continue;
    if (p.scope === "subcategory" && p.subcategoryId) {
      const hit = idMap.find(
        (m) =>
          m.oldSubId === String(p.subcategoryId) &&
          (!p.categoryId || m.oldCategoryId === String(p.categoryId))
      );
      policiesOut.push({
        ...p,
        scope: "category",
        categoryId: hit ? hit.newCategoryId : String(p.subcategoryId),
        typeId: p.typeId || hit?.typeId || null,
        subcategoryId: null,
      });
      continue;
    }

    if (p.scope === "category" && p.categoryId) {
      const children = idMap.filter((m) => m.oldCategoryId === String(p.categoryId));
      if (children.length > 0) {
        for (const ch of children) {
          policiesOut.push({
            ...p,
            id: `${p.id || "lp"}_${ch.newCategoryId}`,
            scope: "category",
            categoryId: ch.newCategoryId,
            typeId: p.typeId || ch.typeId,
            subcategoryId: null,
          });
        }
        continue;
      }
    }

    policiesOut.push({ ...p, subcategoryId: p.subcategoryId ?? null });
  }

  next.version = Math.max(Number(next.version) || 0, 4);
  next.catalogLevels = 2;
  next.masters = next.masters || {};
  next.masters.categories = newCats;
  next.levelPolicies = policiesOut;
  if (idMap.length) next._legacySubcategoryMap = idMap;

  return { config: next, changed: true, idMap };
}
